import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import { RangeConfiguration } from '../types';
import { MultiLanguageInput } from './MultiLanguageInput';

interface QuestionRangeEditorProps {
  rangeConfig: RangeConfiguration;
  onChange: (config: RangeConfiguration) => void;
  disabled?: boolean;
}

export const QuestionRangeEditor: React.FC<QuestionRangeEditorProps> = ({
  rangeConfig,
  onChange,
  disabled = false,
}) => {
  const updateConfig = (field: keyof RangeConfiguration, value: any) => {
    onChange({
      ...rangeConfig,
      [field]: value,
    });
  };

  const updateLabels = (type: 'min' | 'max' | 'intermediate', value: any, key?: number) => {
    const newLabels = { ...rangeConfig.labels };

    if (type === 'intermediate' && typeof key === 'number') {
      if (!newLabels.intermediate) newLabels.intermediate = {};
      newLabels.intermediate[key] = value;
    } else {
      newLabels[type] = value;
    }

    onChange({
      ...rangeConfig,
      labels: newLabels,
    });
  };

  const getStepOptions = () => {
    const range = rangeConfig.max - rangeConfig.min;
    const steps = [];

    // Common step values based on range
    if (range <= 10) {
      steps.push(0.1, 0.5, 1);
    } else if (range <= 100) {
      steps.push(1, 5, 10);
    } else {
      steps.push(10, 25, 50, 100);
    }

    return steps;
  };

  const isValidConfig = () => {
    return rangeConfig.min < rangeConfig.max && rangeConfig.min >= 0;
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Rating Scale Configuration
      </Typography>

      <Stack spacing={3}>
        {/* Range Settings */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Range Settings
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Minimum Value"
                type="number"
                value={rangeConfig.min}
                onChange={(e) => updateConfig('min', parseInt(e.target.value) || 0)}
                disabled={disabled}
                fullWidth
                size="small"
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Maximum Value"
                type="number"
                value={rangeConfig.max}
                onChange={(e) => updateConfig('max', parseInt(e.target.value) || 5)}
                disabled={disabled}
                fullWidth
                size="small"
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>
          </Grid>

          {!isValidConfig() && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Maximum value must be greater than minimum value
            </Typography>
          )}
        </Paper>

        {/* Display Type */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <FormControl component="fieldset" fullWidth disabled={disabled}>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Display Type
            </FormLabel>
            <RadioGroup
              value={rangeConfig.displayType}
              onChange={(e) => updateConfig('displayType', e.target.value)}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControlLabel value="scale" control={<Radio />} label="Number Scale" />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel value="stars" control={<Radio />} label="Star Rating" />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel value="slider" control={<Radio />} label="Slider" />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel value="buttons" control={<Radio />} label="Button Scale" />
                </Grid>
              </Grid>
            </RadioGroup>
          </FormControl>
        </Paper>

        {/* Step Configuration */}
        {rangeConfig.displayType === 'slider' && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Step Size
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {getStepOptions().map((step) => (
                <Chip
                  key={step}
                  label={step}
                  clickable
                  color={rangeConfig.step === step ? 'primary' : 'default'}
                  onClick={() => updateConfig('step', step)}
                  disabled={disabled}
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* Labels */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Labels (Optional)
          </Typography>

          <Stack spacing={2}>
            <MultiLanguageInput
              label="Minimum Label"
              value={rangeConfig.labels?.min || {}}
              onChange={(value) => updateLabels('min', value)}
              placeholder={`${rangeConfig.min}`}
              disabled={disabled}
            />

            <MultiLanguageInput
              label="Maximum Label"
              value={rangeConfig.labels?.max || {}}
              onChange={(value) => updateLabels('max', value)}
              placeholder={`${rangeConfig.max}`}
              disabled={disabled}
            />
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};
